import { useState } from 'react';

import {
  EMPTY_REGISTRATION_FORM,
  validateRegistrationForm,
  type RegistrationFieldErrors,
  type RegistrationFormValues,
} from '../features/auth/application/registrationForm';
import { Alert, Button, Card, ColorSchemeSwitch, SelectField, TextField } from '../shared/components';

import styles from './AuthPages.module.css';

/**
 * Self-report form for someone the import could not match.
 *
 * Validation lives in `registrationForm.ts` and is tested without rendering.
 * This screen shows the result (§5.4).
 */
/**
 * What this screen needs to render a branch chooser: an id and a label.
 *
 * Deliberately not the domain `Branch`. `pages/` may not import `core/`
 * (§3.2), and the boundary checker was right to refuse it — a page that knows
 * the full domain type starts depending on fields it has no business
 * rendering. The composition root maps the domain object down to this.
 */
export interface BranchOption {
  readonly id: string;
  readonly nameAr: string;
}

export interface RegisterPageProps {
  readonly reasonMessageAr: string;
  readonly branches: readonly BranchOption[];
  readonly onSubmit: (values: RegistrationFormValues) => Promise<void>;
  readonly onSignOut: () => void;
  readonly errorAr: string | null;
}

export function RegisterPage({
  reasonMessageAr,
  branches,
  onSubmit,
  onSignOut,
  errorAr,
}: RegisterPageProps) {
  const [values, setValues] = useState<RegistrationFormValues>(EMPTY_REGISTRATION_FORM);
  const [errors, setErrors] = useState<RegistrationFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const set = (field: keyof RegistrationFormValues) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = validateRegistrationForm(values);
    setErrors(result.errors);
    if (!result.valid) return;

    setSubmitting(true);
    void onSubmit(values).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.schemeSlot}>
        <ColorSchemeSwitch />
      </div>

      <h1 className="visually-hidden">تسجيل البيانات</h1>

      <Card title="أدخل بياناتك">
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}

        <Alert severity="info">{reasonMessageAr}</Alert>

        <p className={styles.body}>
          أدخل اسمك الرباعي كما في هويتك، وستراجع الموارد البشرية طلبك.
        </p>

        {/* A real form element: submitting with Enter is how a phone keyboard
            expects to work, and a div with a click handler does not do that. */}
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.formGrid}>
            <TextField
              label="الاسم الأول"
              required
              value={values.firstName}
              onChange={set('firstName')}
              error={errors.firstName}
              autoComplete="given-name"
            />
            <TextField
              label="اسم الأب"
              required
              value={values.fatherName}
              onChange={set('fatherName')}
              error={errors.fatherName}
            />
            <TextField
              label="اسم الجد"
              required
              value={values.grandfatherName}
              onChange={set('grandfatherName')}
              error={errors.grandfatherName}
            />
            <TextField
              label="الجد الثاني واللقب"
              required
              value={values.familyName}
              onChange={set('familyName')}
              error={errors.familyName}
              autoComplete="family-name"
            />
            <TextField
              label="رقم الجوال"
              required
              numeric
              // Opens the numeric keypad on a phone instead of the full
              // keyboard, and `tel` lets the browser offer a saved number.
              inputMode="numeric"
              autoComplete="tel"
              placeholder="7XXXXXXXX"
              value={values.phone}
              onChange={set('phone')}
              error={errors.phone}
              hint="9 أرقام تبدأ بـ 70 أو 71 أو 73 أو 77 أو 78"
            />
            <SelectField
              label="الفرع"
              required
              value={values.branchId}
              onChange={set('branchId')}
              error={errors.branchId}
            >
              <option value="" disabled>
                اختر الفرع…
              </option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.nameAr}
                </option>
              ))}
            </SelectField>

            <div className={styles.spanAll}>
              <div className={styles.actions}>
                <Button type="submit" size="lg" fullWidth loading={submitting}>
                  إرسال الطلب
                </Button>
                <Button variant="ghost" fullWidth onClick={onSignOut}>
                  تسجيل الخروج
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
